/**
 * KnowledgeAtlasPage — dedicated Knowledge Atlas route.
 *
 * Sprint 1 (02-Jul-2026):
 *   • Search.Inc + SI-Agentic wordmarks bumped to fontSize:30, cyan-400
 *     with a soft glow — mirrors the Knowledge Controls page treatment.
 *   • First-visit welcome modal (AtlasOnboardingModal) auto-opens on
 *     initial arrival; re-openable via the "(i)" info pin.
 *   • "Knowledge Atlas" subtitle sits under SI-Agentic (iceberg-blue).
 *   • Cockpit-nav icon retained (operator direction 02-Jul-2026).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, ArrowLeft } from 'lucide-react';
import { CockpitDemoProvider } from '../components/Cockpit/CockpitDemoState';
import { KnowledgeAtlasExplorer } from '../components/KnowledgeAtlas/KnowledgeAtlasExplorer';
import { HoverTip } from '../components/HoverTip';
import { RestoreTooltipDot } from '../components/app/RestoreTooltipDot';
import {
  AtlasOnboardingModal,
  hasSeenAtlasOnboarding,
} from '../components/KnowledgeAtlas/AtlasOnboardingModal';

export default function KnowledgeAtlasPage() {
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const navigate = useNavigate();
  // Handle exposed by KnowledgeAtlasExplorer via forwardRef — lets the
  // top-right KG-icon trigger the shared upload picker that lives
  // (with the tenant state + upload handler) inside the explorer.
  const explorerRef = useRef(null);

  // IJ-269-v3 (2026-08-12) — admin-only upload guard for the KG upload
  // icon. Icon and hover tag remain visible to every signed-in user.
  // Click is a no-op for non-admins (early-return inside handleKgIconClick).
  // Backend documents.py already refuses non-admin uploads with a 403;
  // this front-end change aligns the visible interaction with that gate.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && typeof d.is_admin === 'boolean') {
          setIsAdmin(d.is_admin);
        }
      })
      .catch(() => { /* fail-safe: isAdmin stays false */ });
    return () => { cancelled = true; };
  }, []);

  // Tooltip visibility — persisted in localStorage. Shared key with
  // KnowledgeControls + KG page so dismissing on any SI-Agentic surface
  // silences the same hint everywhere. SPD v5.55, 2026-02-08.
  const [hiddenTooltips, setHiddenTooltips] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('searchinc_hidden_tooltips') || '{}');
    } catch { return {}; }
  });
  const handleHideTooltip = useCallback((id) => {
    setHiddenTooltips((prev) => {
      const next = { ...prev, [id]: true };
      try { localStorage.setItem('searchinc_hidden_tooltips', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);
  const handleRestoreTooltip = useCallback((id) => {
    setHiddenTooltips((prev) => {
      const next = { ...prev };
      delete next[id];
      try { localStorage.setItem('searchinc_hidden_tooltips', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  useEffect(() => {
    // Auto-open on first visit only. localStorage gate lives inside the
    // modal helpers so tests can flip it deterministically.
    if (!hasSeenAtlasOnboarding()) {
      setOnboardingOpen(true);
    }
  }, []);

  const handleKgIconClick = () => {
    // IJ-269-v3 — click is a no-op for non-admins. Icon and hover
    // remain visually identical. Backend enforces the same gate.
    if (!isAdmin) return;
    if (explorerRef.current && explorerRef.current.triggerKgUpload) {
      explorerRef.current.triggerKgUpload();
    }
  };

  return (
    <CockpitDemoProvider>
      <main className="relative min-h-screen bg-[#050C15] text-slate-100">
        {/* Header — visual weight mirrors Knowledge Controls per operator
            direction 02-Jul-2026. Both wordmarks share fontSize:30 and
            the cyan-400 iceberg-blue treatment with a soft glow.
            z-30 (bumped from z-20 on 2026-02-09 per D2.2b) so the
            header's HoverTip tooltips paint above the sibling
            KnowledgeAtlasExplorer (also z-20) — fixes the tooltip-
            behind-topic-controls stacking-context bug in D2 slides 2 & 3. */}
        <header
          data-testid="atlas-page-header"
          className="relative z-30 flex items-center justify-between px-6 pt-6 pb-4 sm:px-12"
        >
          <div className="flex items-center gap-3">
            {/* Composite Back-to-Home affordance (operator D7 · 2026-02-07;
                nav target corrected 2026-07-11 per operator: goes to
                `/` (Search.Inc main landing), not `/knowledge-controls`).
                One clickable element: a blue horizontal arrow drawn to the
                left of an icon so arrow+icon read as a single "← Home"
                mark. Not two separate buttons. */}
            <div className="relative group" data-testid="atlas-page-back-arrow-wrap">
              <button
                type="button"
                data-testid="atlas-page-back-arrow"
                onClick={() => navigate('/')}
                aria-label="Back to Search.Inc"
                className="inline-flex items-center gap-1 rounded-lg p-1 transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
              >
                <ArrowLeft
                  data-testid="atlas-page-back-arrow-icon"
                  className="h-6 w-6 text-cyan-300 drop-shadow-[0_0_6px_rgba(34,211,238,0.55)]"
                  strokeWidth={2.5}
                />
                <img
                  data-testid="atlas-page-cockpit-nav"
                  src="/icons/cockpit_256.png"
                  alt="Search.Inc"
                  draggable={false}
                  className="select-none"
                  style={{
                    width: 39,
                    height: 39,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.35))',
                  }}
                />
              </button>
              <HoverTip
                testId="atlas-page-back-arrow-tag"
                align="bottom-left"
                tooltipId="ka_kc_back"
                hiddenTooltips={hiddenTooltips}
                onHide={handleHideTooltip}
                title="Back to main page"
              />
              {hiddenTooltips['ka_kc_back'] && (
                <div className="absolute -top-1 -right-1 z-10">
                  <RestoreTooltipDot
                    tooltipId="ka_kc_back"
                    hiddenTooltips={hiddenTooltips}
                    onRestore={handleRestoreTooltip}
                    title="Bring back the Knowledge Controls hint"
                  />
                </div>
              )}
            </div>

            {/* Search.Inc brand mark · IJ-175 · 2026-07-30 · new logo image
                replaces the previous text wordmark. Height 32px keeps the
                visual footprint identical to the fontSize:30 wordmark it
                replaces. */}
            <div className="leading-tight" data-testid="atlas-page-searchinc-wordmark">
              <img
                src="/searchinc-logo.png"
                alt="Search.Inc — Actually not evil"
                style={{ height: 42 }}
                className="drop-shadow-[0_0_12px_rgba(34,211,238,0.4)] select-none"
                draggable={false}
                data-testid="atlas-page-searchinc-wordmark-img"
              />
            </div>

            {/* Info (i) pin — re-opens the welcome modal on demand. */}
            <button
              type="button"
              data-testid="atlas-page-info-pin"
              onClick={() => setOnboardingOpen(true)}
              aria-label="Show welcome briefing"
              title="Show welcome briefing"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-cyan-400/30 bg-slate-900/40 text-cyan-300/70 transition-all hover:scale-110 hover:border-cyan-400/70 hover:text-cyan-100"
            >
              <Info className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center gap-4">
            {/* Knowledge Graph upload icon — operator D7 · 2026-02-07.
                Mirrors the exact icon + styling used by KC's KG upload
                button (data-testid=kc-kg-upload-btn). Clicking opens the
                shared file picker that lives inside the explorer, and
                auto-partitions to the currently-selected Topic. */}
            <div className="relative group">
              <button
                type="button"
                data-testid="atlas-page-kg-upload-btn"
                onClick={handleKgIconClick}
                aria-label="Upload to Knowledge Graph"
                className="p-1 transition-transform duration-200 hover:scale-[1.35] focus:outline-none"
              >
                <img
                  src="/icons/add-topic.png"
                  alt="Upload to Knowledge Graph"
                  draggable={false}
                  className="select-none"
                  style={{
                    width: 39,
                    height: 39,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 0 8px rgba(34,211,238,0.4))',
                  }}
                />
              </button>
              <HoverTip
                testId="atlas-page-kg-upload-hover-tag"
                align="bottom-right"
                wide
                tooltipId="ka_kg_upload"
                hiddenTooltips={hiddenTooltips}
                onHide={handleHideTooltip}
                title="Knowledge Graph (KG)"
              >
                <p className="text-lg leading-relaxed text-gray-200">Upload artefacts to create a secure private library that powers smarter search, more accurate answers and advanced AI workflows.</p>
                <p className="mt-3 text-lg leading-relaxed text-gray-200">For Beta we&#39;ve preloaded the KG with AI and Search content so you can explore it immediately.</p>
                <p className="mt-3 text-lg leading-relaxed text-gray-200">Check out Atlas to see how it works.</p>
              </HoverTip>
              {hiddenTooltips['ka_kg_upload'] && (
                <div className="absolute -top-1 -right-1 z-10">
                  <RestoreTooltipDot
                    tooltipId="ka_kg_upload"
                    hiddenTooltips={hiddenTooltips}
                    onRestore={handleRestoreTooltip}
                    title="Bring back the Knowledge Graph hint"
                  />
                </div>
              )}
            </div>

            {/* SI-Agentic wordmark stack — matches Knowledge Controls
                treatment: primary line iceberg-blue @ fontSize:30, "Knowledge Atlas"
                subtitle in the softer cyan tone. */}
            <div className="text-right leading-tight" data-testid="atlas-page-si-agentic-wordmark">
              <div
                className="font-bold tracking-tight text-cyan-400 drop-shadow-[0_0_12px_rgba(34,211,238,0.4)]"
                style={{ fontSize: 30, lineHeight: 1 }}
                data-testid="atlas-page-brand-si-agentic"
              >
                SI-Agentic
              </div>
              <div
                className="mt-1 text-[16px] font-medium leading-tight tracking-wide text-cyan-200/80"
                data-testid="atlas-page-brand-atlas"
              >
                Knowledge Atlas
              </div>
            </div>
          </div>
        </header>

        <KnowledgeAtlasExplorer ref={explorerRef} />

        <p
          data-testid="atlas-page-footer"
          className="relative z-20 mb-6 mt-2 px-6 text-[10px] italic tracking-wide text-[#E8C56F]/70 sm:px-12"
        >
          ▎ Search.Inc — Knowledge Atlas · Proprietary IP
        </p>

        <AtlasOnboardingModal
          open={onboardingOpen}
          onDismiss={() => setOnboardingOpen(false)}
        />
      </main>
    </CockpitDemoProvider>
  );
}
